import { Component, OnInit } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { SaudeResumoModalDatas } from './saude-resumo-modal-datas/saude-resumo-modal-datas';
import {
  DadosConsulta,
  ResumoSaudeService,
  DadosSaude,
} from '../../services/resumo-saude.service';
import moment from 'moment';
import 'moment/locale/pt-br';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-saude-resumo',
  templateUrl: './saude-resumo.html',
  styleUrl: './saude-resumo.scss',
  imports: [MatIconModule, SaudeResumoModalDatas, CommonModule],
})
export class SaudeResumo implements OnInit {
  // --- Estado da página ---
  nomePessoa = ''; 
  dataSelecionada = '';
  dadosSaude: DadosSaude | null = null;
  carregando = true;
  erro: string | null = null;

  // --- Modal ---
  modalOpen = false;
  modalTitle = '';
  modalSubtitle = '';
  modalMode: 'single' | 'multi' = 'single';
  modalOptions: any[] = [];
  selectedId?: string | number;
  selectedIds: Array<string | number> = [];
  modalContext: 'filtroData' | 'classDor' | 'multiExemplo' | null = null;
  datasOptions: any[] = []; // populado pelo carregarDatasConsultas

  constructor(
    private resumoSaudeService: ResumoSaudeService,
    private authService: AuthService,
  ) {}

  async ngOnInit() {
    await this.inicializar();
  }

  async inicializar() {
    this.nomePessoa = this.authService.getUserName();
    await this.carregarDatasConsultas();
    await this.carregarResumo();
  }

  async carregarResumo() {
    this.carregando = true;
    this.erro = null;

    const dados = await this.resumoSaudeService.buscarResumoSaude(
      this.authService.getUserId(),
      this.dataSelecionada,
    );

    this.carregando = false;
    if (!dados) {
      this.erro = `Erro ao carregar o resumo de saúde para a data ${this.formaterDataParaTextoAmigavel(this.dataSelecionada)}`;
      return;
    }

    this.dadosSaude = dados;
  }

  async carregarDatasConsultas() {
    const res: Array<DadosConsulta> =
      await this.resumoSaudeService.buscarDatasConsultas(
        this.authService.getUserId(),
      );

    if (!!res) {
      const datas = res
        .filter((c) => !!c.data)
        .sort((a, b) => (a.data > b.data ? -1 : 1)); // mais recente primeiro

      this.datasOptions = datas.map((c) => ({
        id: c.data,
        label: this.formaterDataParaTextoAmigavel(c.data),
      }));

      this.dataSelecionada = this.datasOptions[0].id;
    }
  }

  get dataExibicao(): string {
    return moment(this.dataSelecionada)
      .locale('pt-br')
      .format('D [de] MMMM [de] YYYY');
  }

  // --- Modal filtro de data ---
  openFiltroData() {
    this.modalContext = 'filtroData';
    this.modalMode = 'single';
    this.modalTitle = 'Filtrar data';
    this.modalSubtitle = 'Últimas atualizações disponíveis.';
    this.modalOptions = this.datasOptions;
    this.selectedId = this.dataSelecionada;
    this.modalOpen = true;
  }


  mapperRiscoFisicoPsicossociaisToComponentSetup(): {
    text: string;
    corPrimaria: string;
    corFundo: string;
  } {
    // 0 = Não avaliado
    // 1 = Baixo
    // 2 = Médio
    // 3 = Alto
    const risco = this.dadosSaude?.movia.riscoFisicoPsicossociais;

    switch (risco) {
      case 0:
        return {
          text: 'Não avaliado',
          corPrimaria: '#26A69A',
          corFundo: '#26A69A14',
        };

      case 1:
        return {
          text: 'Baixo risco para fatores físicos e psicossociais relacionados à dor lombar',
          corPrimaria: '#26A69A',
          corFundo: '#26A69A14',
        };

      case 2:
        return {
          text: 'Médio risco para fatores físicos e psicossociais relacionados à dor lombar',
          corPrimaria: '#FFB029',
          corFundo: '#FFB02914',
        };

      case 3:
        return {
          text: 'Alto risco para fatores físicos e psicossociais relacionados à dor lombar',
          corPrimaria: '#FFB029',
          corFundo: '#FFB02914',
        };

      default:
        return {
          text: '-',
          corPrimaria: '#26A69A',
          corFundo: '#26A69A14',
        };
    }
  }

  mapperDorToComponentSetup(): {
    text: string;
    corFundoIcone: string;
  } {
    const dor = this.dadosSaude?.movia.classificacaoDor;

    switch (dor) {
      case 0:
        return {
          text: '0 - Nenhuma dor',
          corFundoIcone: '#36C547',
        };
      case 1:
        return {
          text: '1 - Pouca dor lombar',
          corFundoIcone: '#9CDD24',
        };
      case 2:
        return {
          text: '2 - Pouca dor lombar',
          corFundoIcone: '#B5E419',
        };
      case 3:
        return {
          text: '3 - Pouca dor lombar',
          corFundoIcone: '#DAEC01',
        };
      case 4:
        return {
          text: '4 - Dor lombar moderada',
          corFundoIcone: '#E3EC00',
        };
      case 5:
        return {
          text: '5 - Dor lombar moderada',
          corFundoIcone: '#FED600',
        };
      case 6:
        return {
          text: '6 - Dor lombar moderada',
          corFundoIcone: '#FAB104',
        };
      case 7:
        return {
          text: '7 - Dor lombar forte',
          corFundoIcone: '#FF6503',
        };
      case 8:
        return {
          text: '8 - Dor lombar forte',
          corFundoIcone: '#F93504',
        };
      case 9:
        return {
          text: '9 - Dor lombar forte',
          corFundoIcone: '#E7001E',
        };
      case 10:
        return {
          text: '10 - Dor lombar insuportável',
          corFundoIcone: '#C50012',
        };
      default:
        return {
          text: '-',
          corFundoIcone: '#36C547',
        };
    }
  }

  mapperPosturaToComponentSetup(): {
    text: string;
    corPrimaria: string;
    corFundo: string;
  } {
    const postura = this.dadosSaude?.movia.melhoraPostura;

    switch (postura) {
      case 4:
        return {
          text: 'Não avaliado',
          corPrimaria: '#0D4DA1',
          corFundo: '#0D4DA114',
        };
      case 3:
        return {
          text: 'Piorou',
          corPrimaria: '#FFB029',
          corFundo: '#FFB02914',
        };
      case 2:
        return {
          text: 'Manteve',
          corPrimaria: '#0D4DA1',
          corFundo: '#0D4DA114',
        };
      case 1:
        return {
          text: 'Melhorou',
          corPrimaria: '#0D4DA1',
          corFundo: '#0D4DA114',
        };
      default:
        return {
          text: '-',
          corPrimaria: '#0D4DA1',
          corFundo: '#0D4DA114',
        };
    }
  }

  formaterDataParaTextoAmigavel(data: string) {
    return moment(data).locale('pt-br').format('D [de] MMMM [de] YYYY');
  }

  closeModal() {
    this.modalOpen = false;
    this.modalContext = null;
  }

  async onConfirmModal(result: any) {
    if (result.mode === 'single') {
      const selected = result.selectedId;

      if (
        this.modalContext === 'filtroData' &&
        selected !== this.dataSelecionada
      ) {
        this.dataSelecionada = selected;
        await this.carregarResumo();
      }

      if (this.modalContext === 'classDor') {
        console.log('Dor escolhida:', selected);
      }
    }

    if (result.mode === 'multi') {
      console.log('Multi selecionado:', result.selectedIds);
    }

    this.closeModal();
  }
}
